import { ChevronDownIcon, ChevronUpIcon } from '@/components/icons';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useConfigStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

interface ToggleTypeAccordionItemProps {
    id: string;
    title: string;
    content: string;
    categoryName: string;
    isExpanded: boolean;
    onToggle: (id: string) => void;
    isHorizontal: boolean;
    tabIndex: number;
    index: number;
}

const ToggleTypeAccordionItem = ({ id, title, content, categoryName, isExpanded, onToggle, isHorizontal, tabIndex, index }: ToggleTypeAccordionItemProps) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isExpanded) return;

        const timer = setTimeout(() => {
            const contentEl = contentRef.current;
            if (!contentEl) return;

            contentEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            contentEl.focus({ preventScroll: true });
        }, 300);

        return () => clearTimeout(timer);
    }, [isExpanded]);

    return (
        <div
            className={clsx(styles.item, {
                [styles['item--horizontal']]: isHorizontal,
                [styles['item--vertical']]: !isHorizontal,
                [styles['item--expanded']]: isExpanded && !contrast,
            })}
        >
            <button
                type="button"
                role="switch"
                data-role={t('common.button.tts.toggle_button')}
                className={clsx(styles.header, 'focus-border', {
                    [styles['header--horizontal']]: isHorizontal,
                    [styles['header--vertical']]: !isHorizontal,
                    [styles['header--expanded']]: isExpanded,
                })}
                onClick={() => onToggle(id)}
                aria-expanded={isExpanded}
                aria-checked={isExpanded}
                tabIndex={tabIndex}
                data-interactive
                data-modal-region
                data-modal-region-order={index}
            >
                <div className="flex flex-row gap-[calc(20*var(--vw-unit))]">
                    <div className={styles['header__category']}>{categoryName}</div>
                    <div className={styles['header__title']}>{title}</div>
                </div>
                <span className={styles['header__icon']}>
                    {isExpanded ? <ChevronUpIcon className={styles['icon--expanded']} /> : <ChevronDownIcon className={styles['icon--collapsed']} />}
                </span>
            </button>

            {isExpanded && (
                <div
                    ref={contentRef}
                    className={clsx('focus-border', styles.content, {
                        [styles['content--horizontal']]: isHorizontal,
                        [styles['content--vertical']]: !isHorizontal,
                        [styles['content--contrast']]: contrast,
                    })}
                    tabIndex={tabIndex}
                    data-interactive
                    aria-label={content}
                >
                    <p className={styles['content__text']}>{content}</p>
                </div>
            )}
        </div>
    );
};

export default ToggleTypeAccordionItem;
